/**
 * User-facing toast copy for Price List form and list actions.
 * Keeps singular/plural and partial-success wording consistent.
 */

import { PRICE_BASE_CHOICES, PRICE_LOGIC_CHOICES } from "../../../constant/data";

const plural = (count, one, many) => (count === 1 ? one : many);

/** API field keys → labels shown in toasts (never expose raw *_id keys). */
const FIELD_LABELS = {
  price_base_id: "Price Base",
  price_base: "Price Base",
  price_logic_id: "Price Logic",
  price_logic: "Price Logic",
  formula_id: "Formula",
  formula: "Formula",
  logic_value: "Logic Value",
  items_data: "Items",
  items_list: "Items",
  supply_warehouse_ids: "Supply Facility",
  supply_warehouses: "Supply Facility",
  channel_ids: "Channel",
  country_codes: "Country",
  customer_ids: "Customer",
  customer_segment_ids: "Customer Segment",
  status_id: "Status",
  name: "Name",
  non_field_errors: null,
};

/** Choice ids → display names used inside validation copy. */
const VALUE_LABELS = Object.fromEntries([
  ...PRICE_BASE_CHOICES.map(c => [c.id, c.name]),
  ...PRICE_LOGIC_CHOICES.map(c => [c.id, c.name]),
  ["draft", "Draft"],
  ["published", "Published"],
  ["increase", "Increase"],
  ["decrease", "Decrease"],
]);

/**
 * Title-case a leftover snake_case API key for display.
 *
 * @param {string} key
 * @return {string}
 */
const titleFromSnake = key => {
  const cleaned = key.endsWith("_id") ? key.slice(0, -3) : key;
  return cleaned
    .split("_")
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

/**
 * Rewrite API validation text for merchants: friendly field names and choice
 * labels instead of snake_case ids (e.g. price_base_id / average_cost).
 *
 * @param {string} text Raw API detail
 * @return {string}
 */
export const humanizeApiDetail = text => {
  if (!text || typeof text !== "string") {
    return text || "";
  }
  let out = text.replace(/\s+/g, " ").trim();

  // "price_base_id: …" → "Price Base: …" (or drop key when message already labeled)
  Object.entries(FIELD_LABELS).forEach(([key, label]) => {
    const keyPattern = new RegExp(`\\b${key}\\s*:\\s*`, "gi");
    if (!label) {
      out = out.replace(keyPattern, "");
      return;
    }
    out = out.replace(keyPattern, `${label}: `);
  });

  // Any remaining snake_case_id: prefix → Title Case
  out = out.replace(/\b([a-z]+(?:_[a-z0-9]+)+)\s*:/gi, (_, key) => {
    if (FIELD_LABELS[key] === null) {
      return "";
    }
    return `${FIELD_LABELS[key] || titleFromSnake(key)}:`;
  });

  // Replace quoted and bare choice ids with display names (longest first).
  Object.entries(VALUE_LABELS)
    .sort((a, b) => b[0].length - a[0].length)
    .forEach(([id, name]) => {
      out = out.replace(new RegExp(`['"“”]${id}['"“”]`, "g"), `“${name}”`);
      out = out.replace(new RegExp(`\\b${id}\\b`, "g"), name);
    });

  // Collapse "Price Base: Price Base …" duplication from key + message label.
  out = out.replace(/\b(Price Base|Price Logic|Formula):\s+\1\b/gi, "$1");

  return out.replace(/\s+/g, " ").trim();
};

/**
 * Resolve a display name for a list row. Never exposes numeric ids.
 *
 * @param {Object=} row
 * @return {string}
 */
export const pricelistDisplayName = row => {
  const name = (row?.name || "").trim();
  if (name) {
    return `"${name}"`;
  }
  return "this price list";
};

/**
 * Map API failure rows to full readable reasons (name when known; never ids).
 *
 * @param {Array<{id: number, detail: string}>} failures
 * @param {Array<Object>=} rows Selected rows (for names)
 * @return {string}
 */
export const formatBulkFailureHints = (failures, rows = []) => {
  const byId = Object.fromEntries((rows || []).map(r => [r.id, r]));
  return (failures || [])
    .map(f => {
      const name = (byId[f.id]?.name || "").trim();
      const detail = humanizeApiDetail(f.detail || "Unknown error");
      // "ABC. Reason…" reads better than `"ABC": reason` for toast UX.
      return name ? `"${name}". ${detail}` : detail;
    })
    .join(" ");
};

/**
 * Toast for form save (create / draft / publish / unpublish).
 *
 * @param {Object} opts
 * @param {boolean} opts.wasCreate
 * @param {string} opts.nextStatus draft | published
 * @param {string=} opts.previousStatus
 * @param {string=} opts.name
 * @return {{type: string, message: string}}
 */
export const formSaveToast = ({ wasCreate, nextStatus, previousStatus, name }) => {
  const label = name?.trim() ? `"${name.trim()}"` : "Price list";

  if (wasCreate && nextStatus === "published") {
    return {
      type: "success",
      message: `${label} created and published. It is now available for orders.`,
    };
  }
  if (wasCreate) {
    return {
      type: "success",
      message: `${label} created as a draft. Publish when you are ready.`,
    };
  }
  if (nextStatus === "published") {
    return {
      type: "success",
      message: `${label} published successfully. It is now available for orders.`,
    };
  }
  if (previousStatus === "published" && nextStatus === "draft") {
    return {
      type: "success",
      message: `${label} unpublished and moved back to draft. It will not apply to new orders.`,
    };
  }
  return {
    type: "success",
    message: `${label} saved as draft.`,
  };
};

/**
 * Toast(s) for bulk publish / unpublish from the list.
 * Prefer a single warning for partial success (better than success+error spam).
 *
 * @param {"publish"|"unpublish"} action
 * @param {{succeeded?: number[], failed?: Array<{id: number, detail: string}>}} result
 * @param {Array<Object>} rows Rows that were attempted
 * @return {{type: string, message: string}|null}
 */
export const bulkStatusToast = (action, result, rows = []) => {
  const ok = result?.succeeded?.length || 0;
  const failures = result?.failed || [];
  const fail = failures.length;
  const verb =
    action === "publish"
      ? { past: "published", noun: "publish", gerund: "publishing" }
      : { past: "unpublished", noun: "unpublish", gerund: "unpublishing" };

  if (ok === 0 && fail === 0) {
    return {
      type: "warning",
      message: `Nothing to ${verb.noun}. Select ${
        action === "publish" ? "draft" : "published"
      } price lists and try again.`,
    };
  }

  if (ok > 0 && fail === 0) {
    if (ok === 1) {
      const row = rows.find(r => r.id === result.succeeded[0]) || rows[0];
      return {
        type: "success",
        message:
          action === "publish"
            ? `${pricelistDisplayName(row)} published. It is now available for orders.`
            : `${pricelistDisplayName(row)} unpublished and moved to draft.`,
      };
    }
    return {
      type: "success",
      message: `${ok} price lists ${verb.past} successfully.`,
    };
  }

  if (ok === 0 && fail > 0) {
    if (fail === 1) {
      const row = rows.find(r => r.id === failures[0].id) || rows[0];
      const detail = humanizeApiDetail(failures[0].detail || "Unknown error");
      return {
        type: "error",
        message: `Could not ${verb.noun} ${pricelistDisplayName(row)}. ${detail}`,
      };
    }
    return {
      type: "error",
      message: `Could not ${verb.noun} ${fail} price lists. ${formatBulkFailureHints(
        failures,
        rows,
      )}`,
    };
  }

  // Partial success — one clear warning.
  return {
    type: "warning",
    message: `${ok} ${verb.past}, ${fail} could not be ${verb.past}. ${formatBulkFailureHints(
      failures,
      rows,
    )}`,
  };
};

/**
 * Toast for list delete (singular / plural / failure).
 *
 * @param {number} count
 * @param {Error=} error
 * @return {{type: string, message: string}}
 */
export const deleteToast = (count, error) => {
  if (error) {
    const detail =
      humanizeApiDetail(
        error?.data?.detail ||
          (typeof error?.data === "object" ? Object.values(error.data).flat().join(" ") : null) ||
          "Please try again.",
      ) || "Please try again.";
    return {
      type: "error",
      message: `Could not delete ${plural(count, "the price list", "the price lists")}. ${detail}`,
    };
  }
  if (count === 1) {
    return { type: "success", message: "Price list deleted." };
  }
  return {
    type: "success",
    message: `${count} price lists deleted.`,
  };
};

/**
 * Flatten API error payload into a toast-friendly string.
 *
 * @param {*=} data
 * @param {string=} fallback
 * @return {string}
 */
export const apiErrorMessage = (data, fallback = "Something went wrong. Please try again.") => {
  if (!data) {
    return fallback;
  }
  if (typeof data === "string") {
    return humanizeApiDetail(data) || fallback;
  }
  if (data.detail) {
    return humanizeApiDetail(String(data.detail)) || fallback;
  }
  if (Array.isArray(data.non_field_errors)) {
    return humanizeApiDetail(data.non_field_errors.join(" ")) || fallback;
  }
  if (typeof data === "object") {
    const parts = Object.entries(data).map(([k, v]) => {
      const text = Array.isArray(v) ? v.join(" ") : String(v);
      if (k === "non_field_errors" || FIELD_LABELS[k] === null) {
        return text;
      }
      const label = FIELD_LABELS[k] || titleFromSnake(k);
      return text.toLowerCase().startsWith(String(label).toLowerCase())
        ? text
        : `${label}: ${text}`;
    });
    if (parts.length) {
      return humanizeApiDetail(parts.join(" ")) || fallback;
    }
  }
  return fallback;
};
